import { appConfig } from '@/config/app.config';
import { ErrorFallback } from '@/components/error/fallback/ErrorFallback';
import { getLogger } from '@/lib/logger';
import { createRootRouteWithContext, Outlet } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { useEffect } from 'react';
import type { AuthStore } from '@/types';
import type { ErrorComponentProps } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';

const logger = getLogger({ scope: 'app.router-error-boundary' });

interface AppRouterContext {
  auth: () => AuthStore;
  queryClient: QueryClient;
}

/*
 * TanStack Router's errorComponent receives { error, reset } while ErrorFallback
 * uses { error, resetErrorBoundary }. The router catches route render/loader
 * errors here before they reach the React error boundary or window.onerror, so
 * this is where they get logged.
 */
function RootErrorComponent({ error, reset }: ErrorComponentProps) {
  useEffect(() => {
    logger.error('Unhandled route error', { error });
  }, [error]);

  return <ErrorFallback error={error} resetErrorBoundary={reset} />;
}

/*
 * Adding the #portal-root id, as recommended per Tanstack documentation
 * https://tanstack.com/router/latest/docs/framework/react/how-to/integrate-shadcn-ui##step-1-set-up-proper-dom-structure
 */
function RootComponent() {
  return (
    <>
      <div id="root-content">
        <Outlet />
      </div>
      <div id="portal-root" />
      {appConfig.isDevelopment && <TanStackRouterDevtools position="bottom-right" />}
    </>
  );
}

export const Route = createRootRouteWithContext<AppRouterContext>()({
  beforeLoad: async ({ context }) => {
    const { isInitialized, initializeAuth } = context.auth();
    if (!isInitialized) {
      await initializeAuth();
    }
  },
  component: RootComponent,
  errorComponent: RootErrorComponent,
});
